// Locale-aware number formatting.
//
// Every user-facing number MUST go through these helpers so it renders in the
// browser's regional settings (grouping + decimal separator). See CLAUDE.md —
// never hardcode a locale, and never show a raw number / `.toFixed()` result.

import { readerLocale } from './locale';

/** Integer or general number with locale grouping (e.g. 1234567 → "1,234,567").
 *
 *  Degrades to an em dash instead of throwing on a null/undefined/NaN input.
 *  A formatter is a LEAF: when it throws mid-render React unmounts the whole
 *  route and the user gets "Something went wrong" instead of the one number
 *  that was missing. That actually happened — the /status console white-screened
 *  on a count that was `undefined` because a list endpoint answered a paginated
 *  envelope where an array was expected. A missing figure should read as
 *  missing, not take the page down with it. */
export const fmtNum = (n: number | null | undefined, opts?: Intl.NumberFormatOptions): string =>
  typeof n === 'number' && Number.isFinite(n) ? n.toLocaleString(readerLocale(), opts) : '—';

/**
 * Fixed-decimal number with the locale's decimal separator — the locale-aware
 * replacement for `n.toFixed(digits)` (e.g. 12.5 → "12,5" in da-DK).
 */
export const fmtDec = (n: number, digits = 1): string =>
  n.toLocaleString(readerLocale(), { minimumFractionDigits: digits, maximumFractionDigits: digits });

/** Percentage from a 0..1 ratio (e.g. 0.42 → "42%", or fmtPct(0.426, 1) → "42,6%"). */
export const fmtPct = (ratio: number, digits = 0): string => `${fmtDec(ratio * 100, digits)}%`;

/** Compact abbreviation with locale decimals: 1234 → "1,2k", 1_200_000 → "1,20M". */
export function fmtCompact(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `${fmtDec(n / 1_000_000, 2)}M`;
  if (Math.abs(n) >= 1_000) return `${fmtDec(n / 1_000, 1)}k`;
  return fmtNum(n);
}

/** Human-readable CI/build duration from ms (locale-aware): 52000 → "52s",
 *  95000 → "1m 35s", 3_725_000 → "1h 2m". Returns "—" for null/invalid. */
export function fmtDuration(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '—';
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${fmtNum(totalSec)}s`;
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m < 60) return s ? `${fmtNum(m)}m ${fmtNum(s)}s` : `${fmtNum(m)}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${fmtNum(h)}h ${fmtNum(mm)}m` : `${fmtNum(h)}h`;
}

/** Duration as a timecode m:ss (or h:mm:ss past an hour): 52000 → "0:52",
 *  95000 → "1:35", 3_725_000 → "1:02:05". Returns "—" for null/invalid. */
export function fmtMinSec(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '—';
  const total = Math.round(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** Bytes → human-readable size with locale decimals (e.g. 1536 → "1,5 KB"). */
export function fmtBytes(bytes: number | null | undefined): string {
  if (bytes == null) return 'Unknown';
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(sizes.length - 1, Math.floor(Math.log(Math.abs(bytes)) / Math.log(k)));
  return `${fmtDec(bytes / Math.pow(k, i), i === 0 ? 0 : 1)} ${sizes[i]}`;
}
